/**
 * 薬歴ページネーション処理の共通関数
 */

import { Yakureki } from '@/shared/types/yakurekiCore'
import { getYakurekiList } from './yakureki'
import { YAKUREKI_PAGINATION } from '@/shared/constants/yakureki'

export interface FetchAllYakurekiPagesOptions {
  /**
   * back の一覧 API はユーザー単位で 10 秒キャッシュされる (`cache_user_api`)。行を消す /
   * 状態を変える書込の直後に取り直すときは true にしてキャッシュを迂回する。素で取り直すと
   * 消したばかりの行を含む古い一覧を書き戻してしまう。
   */
  forceRefresh?: boolean
}

/**
 * 全薬歴データの取得（ページネーション対応）
 *
 * サーバー側のページネーション制限を回避するため、
 * 複数ページに分けてデータを取得し結合する
 *
 * @returns 全薬歴データの配列
 * @throws 初回ページ取得失敗時はエラーをthrow
 */
export const fetchAllYakurekiPages = async (
  options: FetchAllYakurekiPagesOptions = {}
): Promise<Yakureki[]> => {
  const { PAGE_SIZE, MAX_PAGES } = YAKUREKI_PAGINATION
  const { forceRefresh } = options
  let allResults: Yakureki[] = []
  let currentPage = 1

  try {
    // 最初のページを取得して全件数を確認
    const firstResponse = await getYakurekiList({
      page: currentPage,
      page_size: PAGE_SIZE,
      force_refresh: forceRefresh,
    })
    allResults = firstResponse.data.results
    const totalCount = firstResponse.data.count

    // 全件数が1ページで収まる場合は早期リターン
    if (totalCount <= PAGE_SIZE) {
      return allResults
    }

    // 残りのページを取得（無限ループ防止のため上限設定）
    while (allResults.length < totalCount && currentPage < MAX_PAGES) {
      currentPage++

      try {
        const nextResponse = await getYakurekiList({
          page: currentPage,
          page_size: PAGE_SIZE,
          force_refresh: forceRefresh,
        })
        allResults = [...allResults, ...nextResponse.data.results]
      } catch (pageError) {
        // 個別ページの取得失敗は警告のみ、部分的なデータを返す
        console.error(`ページ ${currentPage} の取得に失敗しました:`, pageError)
        console.warn('部分的なデータを返却します')
        break
      }
    }

    // 最大ページ数に到達した場合の警告
    if (currentPage >= MAX_PAGES && allResults.length < totalCount) {
      console.warn(
        `最大ページ数 ${MAX_PAGES} に到達しました。` +
          `取得済み: ${allResults.length}件 / 全体: ${totalCount}件`
      )
    }

    return allResults
  } catch (error) {
    // 初回ページ取得失敗は全体のエラーとして扱う
    console.error('薬歴データの初回取得に失敗しました:', error)
    throw new Error('薬歴データの取得に失敗しました')
  }
}
